'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import * as api from '@/lib/api-client';
import { setAccessToken } from '@/lib/api-client';
import type { User } from '@/lib/api-client';

// Module-level token storage (not state, to avoid re-renders)
let moduleAccessToken: string | null = null;

export interface DeviceVerificationInfo {
  deviceToken: string;
  email: string;
}

/** Auth resolution status. Issue #263 — `user: User | null` alone can't
 *  distinguish "explicitly logged out" from "refresh threw transiently",
 *  which used to let downstream consumers (DashboardGridV2's anon-clear
 *  path) destroy per-user state during outages. */
export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'error';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  /** Coarser-grained auth state for consumers that need to differentiate
   *  a transient failure from a verified anonymous session. `loading` is
   *  the initial refresh; `authenticated` follows a successful refresh
   *  or login; `unauthenticated` follows an explicit logout; `error`
   *  follows a refresh that threw (network blip, 5xx, etc.). */
  authStatus: AuthStatus;
  /** Populated when `authStatus === 'error'` so consumers can surface a
   *  toast / log it / decide retry strategy. Cleared on next successful
   *  refresh or login. */
  authError: Error | null;
  login: (username: string, password: string) => Promise<DeviceVerificationInfo | void>;
  loginWithPayload: (user: User, accessToken: string, showTrialPopup?: boolean, trialPopupTitle?: string, trialPopupMessage?: string) => void;
  logout: () => Promise<void>;
  getToken: () => string | null;
  isAdmin: boolean;
  isPro: boolean;
  isFull: boolean;
  isDemo: boolean;
  refreshUser: () => Promise<void>;
  updateUser: (partial: Partial<User>) => void;
  showTrialPopup: boolean;
  trialPopupTitle: string;
  trialPopupMessage: string;
  clearTrialPopup: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authStatus, setAuthStatus] = useState<AuthStatus>('loading');
  const [authError, setAuthError] = useState<Error | null>(null);
  const [showTrialPopup, setShowTrialPopup] = useState(false);
  const [trialPopupTitle, setTrialPopupTitle] = useState('Welcome! You have a free Pro trial');
  const [trialPopupMessage, setTrialPopupMessage] = useState('');

  const refreshUser = useCallback(async () => {
    try {
      const payload = await api.refreshToken();
      moduleAccessToken = payload.accessToken;
      setAccessToken(payload.accessToken);
      setUser(payload.user);
      setAuthStatus('authenticated');
      setAuthError(null);
      if (payload.showTrialPopup) {
        setShowTrialPopup(true);
        if (payload.trialPopupTitle) setTrialPopupTitle(payload.trialPopupTitle);
        if (payload.trialPopupMessage) setTrialPopupMessage(payload.trialPopupMessage);
      }
      // Renew middleware cookies so server-side route guards stay valid for 7 days
      const maxAge = 7 * 24 * 60 * 60;
      document.cookie = `auth_status=1; path=/; max-age=${maxAge}; SameSite=Lax`;
      if (payload.user.role === 'admin' || payload.user.role === 'staff') {
        document.cookie = `user_role=${payload.user.role}; path=/; max-age=${maxAge}; SameSite=Lax`;
      }
    } catch (err) {
      // Issue #263 — distinguish "no session" (401, intentional) from
      // "transient failure" (network, 5xx). The api-client throws a
      // status-bearing object for HTTP errors; treat 401 as a clean
      // unauthenticated state, anything else as `error` so downstream
      // consumers can choose to retry or preserve per-user state.
      //
      // Only clear `user` + the access token on a real 401. Wake-from-
      // sleep flips the laptop's network for a few seconds while DNS /
      // sockets re-establish; during that window the queued 10-min
      // refresh interval fires and the fetch throws a no-status
      // TypeError. Previously that nulled `user` immediately and the
      // UI flashed "signed off" until the user reloaded (which then
      // succeeded because the refresh cookie was still valid all
      // along). The interval keeps ticking on transient error, so a
      // later attempt will refresh the token and clear authStatus.
      const status = (err as { status?: number })?.status;
      if (status === 401) {
        moduleAccessToken = null;
        setAccessToken(null);
        setUser(null);
        setAuthStatus('unauthenticated');
        setAuthError(null);
      } else {
        setAuthStatus('error');
        setAuthError(err instanceof Error ? err : new Error(String(err)));
      }
    }
  }, []);

  useEffect(() => {
    refreshUser().finally(() => setLoading(false));
  }, [refreshUser]);

  // Keep the access token fresh. signAccessToken mints a 15-min JWT; without
  // a periodic refresh, a tab left open for >15 min silently loses its
  // access token, and every subsequent request lands at the server as
  // "anonymous" (the server catches verifyAccessToken's throw). That broke
  // two things observably: (1) GET /stocks/:ticker/history 1-min requests
  // got downgraded to 5-min with a "non-VIP" log, and (2) the on-demand
  // fresh-quote refresh inside GET /stocks/:ticker (gated on `authed`) was
  // suppressed, so admin's 1-min page refresh showed the same stale DB
  // price until priceSync next wrote.
  //
  // Refresh every 10 min (before the 15-min JWT expiry) as long as a user
  // is signed in. Refresh failure silently logs out via refreshUser's
  // internal catch path, so a permanent session expiry doesn't keep
  // retrying forever.
  useEffect(() => {
    if (!user) return;
    const intervalId = setInterval(() => {
      refreshUser().catch(() => { /* swallowed — refreshUser handles state */ });
    }, 10 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [user, refreshUser]);

  const loginWithPayload = useCallback((u: User, accessToken: string, trialPopup?: boolean, popupTitle?: string, popupMsg?: string) => {
    moduleAccessToken = accessToken;
    setAccessToken(accessToken);
    setUser(u);
    setAuthStatus('authenticated');
    setAuthError(null);
    if (trialPopup) {
      setShowTrialPopup(true);
      if (popupTitle) setTrialPopupTitle(popupTitle);
      if (popupMsg) setTrialPopupMessage(popupMsg);
    }
    const maxAge = 7 * 24 * 60 * 60;
    document.cookie = `auth_status=1; path=/; max-age=${maxAge}; SameSite=Lax`;
    if (u.role === 'admin' || u.role === 'staff') {
      document.cookie = `user_role=${u.role}; path=/; max-age=${maxAge}; SameSite=Lax`;
    }
  }, []);

  const login = useCallback(async (username: string, password: string): Promise<DeviceVerificationInfo | void> => {
    try {
      const payload = await api.login(username, password);
      loginWithPayload(payload.user, payload.accessToken, payload.showTrialPopup, payload.trialPopupTitle, payload.trialPopupMessage);
    } catch (err: unknown) {
      // Check if this is a device verification challenge
      const errObj = err as { code?: string; deviceToken?: string; email?: string };
      if (errObj.code === 'DEVICE_VERIFICATION_REQUIRED' && errObj.deviceToken) {
        return { deviceToken: errObj.deviceToken, email: errObj.email || '' };
      }
      throw err;
    }
  }, [loginWithPayload]);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      // Ignore errors during logout
    }
    moduleAccessToken = null;
    setAccessToken(null);
    setUser(null);
    setAuthStatus('unauthenticated');
    setAuthError(null);
    // Clear auth cookies
    document.cookie = 'auth_status=; path=/; max-age=0';
    document.cookie = 'user_role=; path=/; max-age=0';
  }, []);

  const getToken = useCallback(() => moduleAccessToken, []);

  const updateUser = useCallback((partial: Partial<User>) => {
    setUser((prev) => prev ? { ...prev, ...partial } : prev);
  }, []);

  const clearTrialPopup = useCallback(() => setShowTrialPopup(false), []);

  const isAdmin = user?.role === 'admin' || user?.role === 'staff';
  const isPro = user ? (user.subscriptionTier === 'pro' || user.subscriptionTier === 'full' || user.subscriptionTier === 'vip') : false;
  const isFull = user ? (user.subscriptionTier === 'full' || user.subscriptionTier === 'vip') : false;
  const isDemo = user?.isDemo ?? false;

  return (
    <AuthContext.Provider value={{ user, loading, authStatus, authError, login, loginWithPayload, logout, getToken, isAdmin, isPro, isFull, isDemo, refreshUser, updateUser, showTrialPopup, trialPopupTitle, trialPopupMessage, clearTrialPopup }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
